import { FieldArray, FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input from "src/components/inputs/Input";
import Toggle from "src/components/toggles/Toggle";
import { generateRandomId } from "src/helpers/helpers";
import useNotification from "src/hooks/useNotification";
import useProductList from "src/hooks/useProductList";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { AddIcon } from "src/icons/AddIcon";
import { CloseIcon } from "src/icons/CloseIcon";
import { array, number, object, string } from "yup";
import {
  useCreateService,
  useGetServiceData,
  useUpdateService,
} from "../api-clients";

const SERVICE_SCHEMA = object().shape({
  productId: number().required("This is a required field."),
  quantity: number().required("This is a required field."),
});

const initialServiceValues = {
  id: generateRandomId(),
  productId: null,
  quantity: "",
};

const FORM_FIELDS = {
  NAME: "name",
  PRICE: "price",
  DURATION: "duration",
  REWARD: "reward",
  REMARKS: "remarks",
  STATUS: "status",
  PRODUCTS: "products",
};

const CreateService = ({
  isInModal = false,
  isInViewMode = false,
  onClose,
}: {
  isInModal?: boolean;
  isInViewMode?: boolean;
  onClose?: () => void;
}) => {
  useSetActiveTab(!isInModal && TAB_NAMES.SERVICE_MANAGEMENT);
  const navigate = useNavigate();
  const { serviceId: id } = useParams();

  const serviceId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!serviceId, [serviceId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetServiceLoading, data } = useGetServiceData(serviceId);

  const { data: products } = useProductList();

  const serviceData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateService,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateService(serviceId);

  const { isLoading, isSuccess, execute, error } = useCreateService();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: serviceData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.PRICE]: serviceData?.[FORM_FIELDS.PRICE] ?? "",
      [FORM_FIELDS.DURATION]: serviceData?.[FORM_FIELDS.DURATION] ?? "",
      [FORM_FIELDS.REWARD]: serviceData?.[FORM_FIELDS.REWARD] ?? "",
      [FORM_FIELDS.REMARKS]: serviceData?.[FORM_FIELDS.REMARKS] ?? "",
      [FORM_FIELDS.STATUS]: serviceData?.[FORM_FIELDS.STATUS] ?? false,
      [FORM_FIELDS.PRODUCTS]: serviceData?.[FORM_FIELDS.PRODUCTS] ?? [
        initialServiceValues,
      ],
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.PRICE]: number().required("This is a Required field."),
      // [FORM_FIELDS.DURATION]: number().required("This is a Required field."),
      // [FORM_FIELDS.REWARD]: number().required("This is a Required field."),
      [FORM_FIELDS.PRODUCTS]: array().of(SERVICE_SCHEMA),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateService(data);
        return;
      }
      execute(data);
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched } = formik;

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Service Updated" : "Service Created."
      );
      !isInModal && navigate("/store/services");
      onClose?.();
    }
  }, [
    isInModal,
    isSuccess,
    isUpdateSuccess,
    navigate,
    onClose,
    successNotification,
  ]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetServiceLoading || isUpdateLoading,
    [isGetServiceLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  const getProductsFieldError = useCallback(
    (fieldName: string, index: number) => {
      return (touched[FORM_FIELDS.PRODUCTS] as any)?.[index]?.[fieldName]
        ? (errors[FORM_FIELDS.PRODUCTS] as any)?.[index]?.[fieldName]
        : undefined;
    },
    [errors, touched]
  );

  const getMaximumProductQuantity = useCallback(
    (index: number) => {
      const selectedProduct = formik.values.products[index]?.productId;

      const product = products.find(
        (product: any) => selectedProduct === product.productId
      );

      return product?.quantity;
    },
    [formik.values.products, products]
  );

  const getQuantityFieldError = useCallback(
    (index: number) => {
      const requiredError = getProductsFieldError("quantity", index);
      const quantity = values[FORM_FIELDS.PRODUCTS]?.[index]?.["quantity"];
      const limit = getMaximumProductQuantity(index);
      if (requiredError) {
        return requiredError;
      }

      return quantity > limit ? "Exceeded" : "";
    },
    [getMaximumProductQuantity, getProductsFieldError, values]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Service" showBreadcrumb />
      <FormikProvider value={formik}>
        <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] md:px-[42px] h-fit">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Service Name"
              placeholder="Service Name"
              name={FORM_FIELDS.NAME}
              value={values[FORM_FIELDS.NAME] as string}
              error={getFieldError(FORM_FIELDS.NAME)}
              onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
              required
              disabled={isFieldDisabled}
            />
            <Input
              type="number"
              label="Price (₹)"
              placeholder="Price"
              name={FORM_FIELDS.PRICE}
              value={values[FORM_FIELDS.PRICE] as string}
              error={getFieldError(FORM_FIELDS.PRICE)}
              onChange={({ target: { value } }) =>
                setFieldValue(FORM_FIELDS.PRICE, value ? Number(value) : "")
              }
              required
              disabled={isFieldDisabled}
            />
            <Input
              type="number"
              label="Duration (days)"
              placeholder="Duration"
              name={FORM_FIELDS.DURATION}
              value={values[FORM_FIELDS.DURATION] as string}
              error={getFieldError(FORM_FIELDS.DURATION)}
              onChange={({ target: { value } }) =>
                setFieldValue(FORM_FIELDS.DURATION, parseInt(value))
              }
              disabled={isFieldDisabled}
            />
            <Input
              type="number"
              label="Reward"
              placeholder="Reward"
              name={FORM_FIELDS.REWARD}
              value={values[FORM_FIELDS.REWARD] as string}
              error={getFieldError(FORM_FIELDS.REWARD)}
              onChange={({ target: { value } }) =>
                setFieldValue(FORM_FIELDS.REWARD, parseInt(value))
              }
              disabled={isFieldDisabled}
            />
            <Input
              label="Remarks"
              placeholder="Remarks"
              name={FORM_FIELDS.REMARKS}
              value={values[FORM_FIELDS.REMARKS] as string}
              error={getFieldError(FORM_FIELDS.REMARKS)}
              onChange={(e) =>
                setFieldValue(FORM_FIELDS.REMARKS, e.target.value)
              }
              disabled={isFieldDisabled}
            />
            <Toggle
              label="Status"
              checked={values[FORM_FIELDS.STATUS] as boolean}
              onToggle={(val) => [setFieldValue(FORM_FIELDS.STATUS, val)]}
              disabled={isFieldDisabled}
              showToggleStatus
            />
          </div>

          <div className="bg-[#F8F7F7] rounded-lg p-4 overflow-x-scroll scrollbar-thin">
            <table className="border-none bg-transparent">
              <tbody>
                <FieldArray name={FORM_FIELDS.PRODUCTS}>
                  {({ insert, remove, push }) => {
                    return (
                      <>
                        {values[FORM_FIELDS.PRODUCTS].map(
                          (item: any, index: number, arr: any) => {
                            const hasMultipleRows = arr.length > 1;
                            return (
                              <tr
                                className="border-none align-top flex items-start justify-between h-full"
                                key={item.id}
                              >
                                <td className="w-full h-full">
                                  <Dropdown
                                    label="Product Name"
                                    placeholder="Select Product"
                                    className={"w-full bg-white"}
                                    options={products}
                                    value={
                                      values[FORM_FIELDS.PRODUCTS]?.[index]
                                        ?.productId as string
                                    }
                                    onChange={(val) =>
                                      setFieldValue(
                                        `${FORM_FIELDS.PRODUCTS}.${index}.productId`,
                                        val
                                      )
                                    }
                                    disabled={isFieldDisabled}
                                    error={getProductsFieldError(
                                      "productId",
                                      index
                                    )}
                                    required
                                  />
                                </td>
                                <td className="w-full h-full">
                                  <Input
                                    name="quantity"
                                    type="number"
                                    label="Quantity"
                                    placeholder="Select Quantity"
                                    className={"w-full [&>div>div]:bg-white"}
                                    value={
                                      values[FORM_FIELDS.PRODUCTS]?.[index]?.[
                                        "quantity"
                                      ] as string
                                    }
                                    onChange={({ target: { value } }) =>
                                      setFieldValue(
                                        `${FORM_FIELDS.PRODUCTS}.${index}.quantity`,
                                        value ?? ""
                                      )
                                    }
                                    disabled={isFieldDisabled}
                                    error={getQuantityFieldError(index)}
                                    max={getMaximumProductQuantity(index)}
                                    required
                                  />
                                </td>

                                {!isInViewMode && hasMultipleRows && (
                                  <td className="flex items-end h-full w-full sm:w-[7%]">
                                    <div
                                      className="flex h-full justify-center items-center top-4 mt-[38px] cursor-pointer"
                                      onClick={() => remove(index)}
                                    >
                                      <CloseIcon color="#B22234" />
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          }
                        )}
                        {!isInViewMode && (
                          <Button
                            text={"Add More"}
                            className="mt-4 ml-2"
                            icon={(color) => <AddIcon color={"#000000"} />}
                            onClick={() => push(initialServiceValues)}
                          />
                        )}
                      </>
                    );
                  }}
                </FieldArray>
              </tbody>
            </table>
          </div>
          <Button
            text={isLoading ? "Loading..." : "Submit"}
            onClick={handleSubmit}
            primary
            className="h-[40px] px-10"
            isDisabled={isFieldDisabled}
          />
        </div>
      </FormikProvider>
    </div>
  );
};

export default CreateService;

const dummyMembershipOPtions = [
  {
    label: "Gold",
    value: 1,
  },
  {
    label: "Silver",
    value: 2,
  },
];
